import { useState, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import ErrorState from '@zenbusiness/zui/ErrorState';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';

import { useChangeTypeReasons } from '../../../hooks/useChangeTypeReasons';
import { CANCEL_FLOW_TYPES, getCancelClickstreamProps, subscriptionContextPropType } from '../utils/cancelFlowConfig';

const MAX_DESCRIPTION_LENGTH = 255;

function NormalWeightChunks(chunks) {
  return (
    <Text component="span" weight="regular">
      {chunks}
    </Text>
  );
}

export function SubscriptionCancelReason({
  cancelFlowType,
  changeReason,
  changeDescription,
  onChangeReason,
  onChangeReasonDisplay,
  onChangeDescription,
  subscriptionContext,
}) {
  const { formatMessage } = useIntl();
  const [descriptionError, setDescriptionError] = useState(false);

  const trackReasonSelected = useClickstreamCallback(
    'buttonPressed',
    getCancelClickstreamProps(cancelFlowType, 'reasonSelected', subscriptionContext),
  );

  const { data, loading, hasError } = useChangeTypeReasons('cancel_renewal');

  const reasons = (data?.cancelRenewal || []).filter((r) => r.show);

  const handleReasonChange = useCallback(
    (e) => {
      const { value } = e.target;
      onChangeReason(value);
      const selected = reasons.find((r) => r.value === value);
      onChangeReasonDisplay?.(selected?.display ?? '');
      trackReasonSelected({
        elementValue: value,
        elementText: selected?.display ?? value,
      });
    },
    [onChangeReason, onChangeReasonDisplay, reasons, trackReasonSelected],
  );

  const handleDescriptionChange = (e) => {
    onChangeDescription(e.target.value);
    if (descriptionError && e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescriptionError(false);
    }
  };

  const setDescriptionErrorIfOverMax = (value) => {
    if (value.length > MAX_DESCRIPTION_LENGTH) {
      setDescriptionError(true);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" p={6}>
        <LoadingSpinner aria-label={formatMessage({ id: 'dashboard.subscriptionCancel.reason.loading.ariaLabel' })} />
      </Stack>
    );
  }

  if (hasError) {
    return (
      <ErrorState
        title={formatMessage({ id: 'dashboard.subscriptionCancel.error.loading.title' })}
        message={formatMessage({ id: 'dashboard.subscriptionCancel.error.loading.message' })}
      />
    );
  }

  return (
    <Stack gap={5}>
      <Stack gap={1.5}>
        <Text size="2xl" weight="bold" family="secondary">
          <FormattedMessage id="dashboard.subscriptionCancel.reason.heading" />
        </Text>
        <Text size="md" color="secondary">
          <FormattedMessage id="dashboard.subscriptionCancel.reason.subheading" />
        </Text>
      </Stack>

      <FormControl>
        <Stack gap={1}>
          <Text size="lg" weight="bold">
            <FormattedMessage
              id="dashboard.subscriptionCancel.reason.labelRequired"
              values={{ normal: NormalWeightChunks }}
            />
          </Text>
          <RadioGroup value={changeReason} onChange={handleReasonChange}>
            {reasons.map((reason) => (
              <FormControlLabel key={reason.value} value={reason.value} control={<Radio />} label={reason.display} />
            ))}
          </RadioGroup>
        </Stack>
      </FormControl>

      <Stack gap={2}>
        <Stack gap={0.5}>
          <Text size="lg" weight="bold">
            <FormattedMessage id="dashboard.subscriptionCancel.reason.feedbackTitle" />
          </Text>
          <Text size="sm" color="tertiary">
            <FormattedMessage id="dashboard.subscriptionCancel.reason.feedbackDescription" />
          </Text>
        </Stack>
        <TextField
          multiline
          minRows={6}
          placeholder={formatMessage({ id: 'dashboard.subscriptionCancel.reason.feedbackPlaceholder' })}
          value={changeDescription}
          onChange={handleDescriptionChange}
          onBlur={(e) => setDescriptionErrorIfOverMax(e.target.value)}
          error={descriptionError}
          helperText={`${changeDescription.length}/${MAX_DESCRIPTION_LENGTH}`}
        />
      </Stack>
    </Stack>
  );
}

SubscriptionCancelReason.propTypes = {
  cancelFlowType: PropTypes.oneOf(Object.values(CANCEL_FLOW_TYPES)).isRequired,
  changeReason: PropTypes.string.isRequired,
  changeDescription: PropTypes.string.isRequired,
  onChangeReason: PropTypes.func.isRequired,
  onChangeReasonDisplay: PropTypes.func,
  onChangeDescription: PropTypes.func.isRequired,
  subscriptionContext: subscriptionContextPropType,
};
